import React, { useEffect, useRef, useState } from 'react';

import { Link as RouterLink, useLocation } from 'react-router-dom';
import dayjs from '~/timeInit';

import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  Link,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';
import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';
import { formatTimestamp } from '~/components/GHADiscounting/utils';
import GHABulkOperationDialog from '~/components/GHAPropertyManagement/GHABulkOperationDialog';
import { getPropertyManagementPath } from '~/components/GoogleAdsHotelBidding/propertyManagementLinks';

import {
  useBulkUpsertGHABlockedIdentities,
  useDeleteGHABlockedIdentity,
  useGHABlockedIdentities,
  useUpsertGHABlockedIdentity,
} from '~/hooks/apis/connection-google/useGHABlockedIdentities';
import useAppNavigation from '~/hooks/useAppNavigation';
import useDebounced from '~/hooks/useDebounced';

import type { GHABlockedIdentity } from '~/services/ConnectionGoogleService';

import BlockedIdentityDialog from './BlockedIdentityDialog';
import { GHA_BLOCKED_IDENTITY_EVIDENCE_FIELDS, GHA_EMAIL_DOMAIN_RULES_PAGE_SIZE_OPTIONS } from './constants';
import { parseBlockedIdentityFilterParams, parsePaginationParams, toExpiryDate } from './utils';

// Shown in their own columns, so left out of the evidence tooltip.
const EVIDENCE_IN_COLUMNS = new Set(['hotel', 'propertyId', 'caseRef']);

// Rows stay one height so a screen shows about ten; long text is clamped and shown in full on hover.
const ROW_HEIGHT = 64;
const clampTwoLines = { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' };

const getColumns = (
  onEdit: (identity: GHABlockedIdentity) => void,
  onDelete: (identity: GHABlockedIdentity) => void,
  isDeleting: boolean,
): Array<GridColDef<GHABlockedIdentity>> => [
  {
    field: 'value',
    headerName: 'Email',
    flex: 1,
    minWidth: 240,
    renderCell: ({ row }) => (
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" noWrap>
          {row.value}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {row.blacklistExempt && (
            <Chip size="small" variant="outlined" label="Exempt (test)" sx={{ height: 18, fontSize: 11 }} />
          )}
          {row.evidence?.caseRef != null && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {row.evidence.caseRef}
            </Typography>
          )}
        </Stack>
      </Box>
    ),
  },
  {
    field: 'hotel',
    headerName: 'Hotel',
    flex: 0.8,
    minWidth: 270,
    sortable: false,
    renderCell: ({ row }) => {
      const hotel = row.evidence?.hotel;
      const propertyId = row.evidence?.propertyId;
      if (hotel == null && propertyId == null) {
        return (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        );
      }
      return (
        <Box sx={{ minWidth: 0 }}>
          {hotel != null && (
            <Typography variant="body2" noWrap title={hotel}>
              {hotel}
            </Typography>
          )}
          {propertyId != null && (
            <Link
              component={RouterLink}
              to={getPropertyManagementPath(propertyId)}
              variant="caption"
              display="block"
              noWrap
            >
              {propertyId}
            </Link>
          )}
        </Box>
      );
    },
  },
  {
    field: 'reason',
    headerName: 'Reason',
    flex: 1.2,
    minWidth: 220,
    renderCell: ({ row }) => (
      <Tooltip title={row.reason ?? ''}>
        <Typography variant="body2" color="text.secondary" sx={clampTwoLines}>
          {row.reason ?? '—'}
        </Typography>
      </Tooltip>
    ),
  },
  {
    field: 'evidence',
    headerName: 'Evidence',
    width: 110,
    sortable: false,
    renderCell: ({ row }) => {
      const present = GHA_BLOCKED_IDENTITY_EVIDENCE_FIELDS.filter(
        ({ key }) => !EVIDENCE_IN_COLUMNS.has(key) && row.evidence?.[key] != null,
      );
      if (present.length === 0) {
        return (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        );
      }
      return (
        <Tooltip
          title={
            <Box>
              {present.map(({ key, label }) => (
                <Typography key={key} variant="caption" display="block">
                  {label}: {row.evidence?.[key]}
                </Typography>
              ))}
            </Box>
          }
        >
          <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'underline dotted' }}>
            {present.length} {present.length === 1 ? 'detail' : 'details'}
          </Typography>
        </Tooltip>
      );
    },
  },
  {
    field: 'expiresAt',
    headerName: 'Expires',
    width: 170,
    renderCell: ({ row }) => {
      if (row.expiresAt == null) {
        return (
          <Typography variant="body2" color="text.secondary">
            Never
          </Typography>
        );
      }
      // Expired rows only show with the toggle on; they stay for the audit trail but never match.
      return new Date(row.expiresAt) <= new Date() ? (
        <Chip size="small" variant="outlined" label={`Expired ${toExpiryDate(row.expiresAt)}`} />
      ) : (
        <Typography variant="body2">{toExpiryDate(row.expiresAt)}</Typography>
      );
    },
  },
  {
    field: 'createdAt',
    headerName: 'Added',
    type: 'dateTime',
    width: 150,
    valueGetter: (value) => dayjs(value).toDate(),
    renderCell: ({ row }) => (
      <Tooltip
        title={`Created by ${row.createdBy}, ${formatTimestamp(row.createdAt)}${
          row.updatedBy != null ? ` · Updated by ${row.updatedBy}, ${formatTimestamp(row.updatedAt)}` : ''
        }`}
      >
        <Box>
          <Typography variant="body2">{dayjs(row.createdAt).format('D MMM YYYY')}</Typography>
          <Typography variant="caption" color="text.secondary" display="block" noWrap>
            {row.createdBy.split('@')[0]}
          </Typography>
        </Box>
      </Tooltip>
    ),
  },
  {
    field: 'actions',
    headerName: '',
    width: 96,
    sortable: false,
    align: 'right',
    renderCell: ({ row }) => (
      <>
        <Tooltip title="Edit">
          <IconButton size="small" aria-label={`Edit ${row.value}`} onClick={() => onEdit(row)}>
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <span>
            <IconButton
              size="small"
              color="error"
              aria-label={`Delete ${row.value}`}
              disabled={isDeleting}
              onClick={() => onDelete(row)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </>
    ),
  },
];

export default function BlockedIdentitiesPanel() {
  const location = useLocation();
  const { push, replace } = useAppNavigation();
  const searchParams = new URLSearchParams(location.search);
  const paginationModel = parsePaginationParams(searchParams);
  // Page and the expired toggle live in the URL like the domain-rules tab; the search text does not,
  // because it is often a whole address.
  const filters = parseBlockedIdentityFilterParams(searchParams);

  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [identityToEdit, setIdentityToEdit] = useState<GHABlockedIdentity | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const showConfirmDialog = useConfirmDialog();
  const debouncedSearch = useDebounced(search.trim(), 300);

  // A new search starts again from the first page. Replaced rather than pushed, so typing does not
  // leave a history entry per pause.
  const searchedFor = useRef(debouncedSearch);
  useEffect(() => {
    if (searchedFor.current === debouncedSearch) {
      return;
    }
    searchedFor.current = debouncedSearch;
    const params = new URLSearchParams(location.search);
    params.set('page', '0');
    replace({ search: params.toString() });
  }, [debouncedSearch, location.search, replace]);

  const { data, isFetching, error } = useGHABlockedIdentities({
    value: debouncedSearch === '' ? undefined : debouncedSearch,
    includeExpired: filters.includeExpired ? true : undefined,
    page: paginationModel.page + 1,
    limit: paginationModel.pageSize,
  });
  const upsertMutation = useUpsertGHABlockedIdentity();
  const deleteMutation = useDeleteGHABlockedIdentity();
  const bulkMutation = useBulkUpsertGHABlockedIdentities();

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    const params = new URLSearchParams(location.search);
    params.set('page', String(model.page));
    params.set('pageSize', String(model.pageSize));
    push({ search: params.toString() });
  };

  const handleIncludeExpiredChange = (includeExpired: boolean) => {
    const params = new URLSearchParams(location.search);
    if (includeExpired) {
      params.set('includeExpired', 'true');
    } else {
      params.delete('includeExpired');
    }
    params.set('page', '0');
    push({ search: params.toString() });
  };

  const handleEdit = (identity: GHABlockedIdentity) => {
    setIdentityToEdit(identity);
    setDialogOpen(true);
  };

  const handleDelete = async (identity: GHABlockedIdentity) => {
    const confirmed = await showConfirmDialog({
      title: `Unblock ${identity.value}?`,
      description: 'This person is classified by the domain rules again, like any other shopper.',
    });
    if (confirmed) {
      deleteMutation.mutate(identity);
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setIdentityToEdit(null);
    setSubmitError(null);
  };

  if (error) {
    return <Alert severity="error">Failed to load blocked identities</Alert>;
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            label="Search email"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={filters.includeExpired}
                onChange={(_, checked) => handleIncludeExpiredChange(checked)}
              />
            }
            label="Show expired"
          />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button variant="text" startIcon={<CloudUploadIcon />} onClick={() => setBulkOpen(true)}>
            Bulk upload
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            Block identity
          </Button>
        </Stack>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        Each row denies one person the GHA discount at every property, whatever their email domain. An address within
        two characters of a blocked one is flagged as uncertain. Mark internal test accounts exempt, so their bookings
        never blacklist the property.
      </Alert>

      <DataGrid
        rows={data?.result ?? []}
        columns={getColumns(handleEdit, handleDelete, deleteMutation.isPending)}
        getRowId={(row) => row.id}
        rowHeight={ROW_HEIGHT}
        pageSizeOptions={GHA_EMAIL_DOMAIN_RULES_PAGE_SIZE_OPTIONS}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        paginationMode="server"
        rowCount={data?.total ?? 0}
        loading={isFetching}
        disableRowSelectionOnClick
        disableColumnMenu
        slots={{ pagination: GridPagination }}
        sx={{ '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' } }}
        autoHeight
      />

      <BlockedIdentityDialog
        open={dialogOpen}
        initialIdentity={identityToEdit}
        isSaving={upsertMutation.isPending}
        submitError={submitError}
        onClose={closeDialog}
        onSubmit={async (value, input) => {
          setSubmitError(null);
          try {
            await upsertMutation.mutateAsync({ id: identityToEdit?.id, value, input });
            closeDialog();
          } catch (submitException) {
            // Malformed and placeholder addresses come back as 400s with the reason — show verbatim.
            setSubmitError(submitException instanceof Error ? submitException.message : 'Failed to save');
          }
        }}
      />

      <GHABulkOperationDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        title="Bulk upload blocked identities"
        csvHeader="kind,value,reason,expires_at,ip,booking_id"
        csvRows={[
          'email,name@gmail.com,OTA test booking,2027-10-01,49.36.138.227,441431481',
          'email,other@yahoo.com,,,,',
        ]}
        csvNotes={
          <>
            • Required columns <strong>kind,value,reason</strong>; kind is email and reason may be empty
            <br />• Optional, in any order: expires_at (a date), and the evidence columns{' '}
            {GHA_BLOCKED_IDENTITY_EVIDENCE_FIELDS.map(({ column }) => column).join(', ')}
            <br />• Each row creates or overwrites that address. Any bad row rejects the whole file.
          </>
        }
        submitButtonLabel="Upload"
        successMessage="Blocked identities uploaded."
        // The mutation's snackbar reports the created/updated counts, and the list refetches, so
        // the dialog's own "refresh the page" success copy is not needed.
        showSuccessAlert={false}
        showSuccessSnackbar={false}
        onSubmit={async (file) => {
          await bulkMutation.mutateAsync(file);
        }}
      />
    </Box>
  );
}
